/**
 * Teacher-style coaching lessons — pure data for Canvas / SVG animations.
 * Shown before (or between) class practice so the student learns *how*, not only *what*.
 */

export type CoachSceneId =
  | "posture"
  | "hand-c-position"
  | "lh-mirror"
  | "direction-ltr-rtl"
  | "thumb-tuck"
  | "black-keys"
  | "chord-shape"
  | "blind-focus";

export interface CoachLesson {
  id: string;
  title: string;
  /** Stages that commonly need this tip. */
  stages: number[];
  scene: CoachSceneId;
  /** Spoken/teacher bullets shown under the animation. */
  bullets: string[];
  /** Approx seconds for auto-advance hint (UI may ignore). */
  durationSec: number;
}

export const COACH_LESSONS: CoachLesson[] = [
  {
    id: "coach-posture",
    title: "How to Sit at the Piano",
    stages: [1],
    scene: "posture",
    bullets: [
      "Sit on the front half of the bench — not slumped back.",
      "Feet flat (or left foot ready for the pedal later); spine tall.",
      "Elbows slightly in front of the torso; forearms roughly level with the keys.",
      "Shoulders soft — tension kills even tone.",
    ],
    durationSec: 12,
  },
  {
    id: "coach-c-position",
    title: "Right-Hand C Position",
    stages: [1],
    scene: "hand-c-position",
    bullets: [
      "Thumb (1) rests on Middle C — the white key left of the two black keys.",
      "Fingers 2–5 cover D E F G — one finger per key, curved like holding a small ball.",
      "Wrists float; don’t collapse or lock.",
      "Only the fingertip pads touch — nails off the ivory.",
    ],
    durationSec: 14,
  },
  {
    id: "coach-lh-mirror",
    title: "Left-Hand Mirror Position",
    stages: [1],
    scene: "lh-mirror",
    bullets: [
      "LH mirrors RH an octave lower: finger 5 on C, thumb (1) toward G.",
      "Keep the same soft curve — bass doesn’t mean pressing harder.",
      "Eyes can glance, but train the hand to feel the landmarks.",
    ],
    durationSec: 12,
  },
  {
    id: "coach-direction",
    title: "Left→Right and Right→Left",
    stages: [1, 2],
    scene: "direction-ltr-rtl",
    bullets: [
      "Ascending (low→high) = left to right on the keyboard.",
      "Descending (high→low) = right to left — don’t twist the arm; walk the fingers.",
      "Keep the forearm moving with the hand so wrists stay aligned.",
    ],
    durationSec: 12,
  },
  {
    id: "coach-thumb-tuck",
    title: "Thumb Tuck Across Octaves",
    stages: [2],
    scene: "thumb-tuck",
    bullets: [
      "After 1–2–3 of a scale, the thumb tucks under to continue ascending.",
      "Prepare early: as finger 3 plays, thumb slides under toward the next white key.",
      "Wrist stays quiet — the tuck is quiet and curved, not a yank.",
      "Descending: finger 3 (or 4) crosses over the thumb smoothly.",
    ],
    durationSec: 14,
  },
  {
    id: "coach-black-keys",
    title: "Finding Black Keys",
    stages: [2],
    scene: "black-keys",
    bullets: [
      "Black keys live in groups of two and three — use them as landmarks.",
      "F♯ sits between F and G (group of three’s left side).",
      "B♭ sits between A and B — for flats, think “one white left of the natural.”",
      "Play black keys with a slightly higher finger approach; tip still curved.",
    ],
    durationSec: 12,
  },
  {
    id: "coach-chord-shape",
    title: "Building a Triad Shape",
    stages: [3],
    scene: "chord-shape",
    bullets: [
      "A major triad = root + major 3rd + perfect 5th (C–E–G).",
      "Drop all three fingers together — one shape, one attack.",
      "Inversions recycle the same three notes in a new order — hand shape shifts, ear stays on the chord.",
    ],
    durationSec: 12,
  },
  {
    id: "coach-blind-focus",
    title: "Playing Without Looking",
    stages: [4, 5],
    scene: "blind-focus",
    bullets: [
      "Find Middle C once, then trust muscle memory landmarks (two-black / three-black groups).",
      "Keep eyes on the music or closed briefly in drill — glance only to recover.",
      "Slow accuracy beats fast guessing; speed comes after the map is in the hands.",
    ],
    durationSec: 12,
  },
];

/** Default teacher intros attached to classes (can be overridden later). */
export const CLASS_COACH_MAP: Record<string, string[]> = {
  "class-1-1": ["coach-posture", "coach-c-position"],
  "class-1-2": ["coach-lh-mirror"],
  "class-1-3": ["coach-direction"],
  "class-1-5": ["coach-c-position", "coach-direction"],
  "class-1-7": ["coach-direction"],
  "class-2-1": ["coach-thumb-tuck"],
  "class-2-2": ["coach-black-keys"],
  "class-2-4": ["coach-thumb-tuck"],
  "class-3-1": ["coach-chord-shape"],
  "class-3-2": ["coach-chord-shape"],
  "class-4-2": ["coach-blind-focus"],
  "class-4-6": ["coach-blind-focus"],
  "class-5-2": ["coach-thumb-tuck", "coach-blind-focus"],
};

/** Stage-wide tips shown the first time that stage is entered (UI). */
export const STAGE_COACH_MAP: Record<number, string[]> = {
  1: ["coach-posture", "coach-c-position"],
  2: ["coach-thumb-tuck", "coach-black-keys"],
  3: ["coach-chord-shape"],
  4: ["coach-blind-focus"],
  5: ["coach-thumb-tuck", "coach-blind-focus"],
};

export function getCoachLesson(id: string): CoachLesson | undefined {
  return COACH_LESSONS.find((l) => l.id === id);
}

export function getCoachLessonsForClass(classId: string): CoachLesson[] {
  const ids = CLASS_COACH_MAP[classId] ?? [];
  return ids.map(getCoachLesson).filter((x): x is CoachLesson => !!x);
}

export function getCoachLessonsForStage(stage: number): CoachLesson[] {
  const ids = STAGE_COACH_MAP[stage] ?? [];
  return ids.map(getCoachLesson).filter((x): x is CoachLesson => !!x);
}
